/**
 * Synthesis primitives shared by every `src/audio/synth/*` module.
 *
 * Two kinds of thing live here:
 *
 *  1. **Bakers** — pure JS that fills an `AudioBuffer` with sample data at init time
 *     (noise banks, the reverb impulse response, the glottal excitation, the crowd bed).
 *     Everything expensive and textural is baked ONCE so the runtime never has to.
 *  2. **Shot helpers** — `ShotBuilder` plus envelope/ramp writers used to assemble a one-shot
 *     node graph. `ShotBuilder` claims every node it makes into the calling voice record, so
 *     `AudioSystem.dispose()` can sever the whole subsystem deterministically.
 *
 * Three-free and DOM-free: the only host object touched is the `AudioContext`-like value passed
 * in, so `tests/unit` can exercise all of this with a stub.
 */

import { clamp, clamp01, TAU } from '../core/index.js';

/**
 * Web Audio's `exponentialRampToValueAtTime` refuses a zero target, so every envelope decays
 * to this instead of silence. -80 dB is inaudible under any mix.
 */
export const EPS = 1e-4;

/** Shortest attack the audio thread renders cleanly; below this a ramp is a step. */
const MIN_ATTACK = 0.0004;

// ---------------------------------------------------------------------------
// Scalar helpers
// ---------------------------------------------------------------------------

export function dbToGain(db) {
  return Math.pow(10, db / 20);
}

export function gainToDb(g) {
  return 20 * Math.log10(Math.max(EPS, g));
}

/** Frequency ratio for a semitone offset. */
export function semis(n) {
  return Math.pow(2, n / 12);
}

/** Frequency ratio for a cent offset. */
export function cents(n) {
  return Math.pow(2, n / 1200);
}

export function midiToHz(m) {
  return 440 * Math.pow(2, (m - 69) / 12);
}

/**
 * Deterministic hash-based pseudo-random in [0,1) from three integers. Used where the same
 * query must give the same answer every time it is asked — the music sequencer re-derives the
 * pattern for a bar on every look-ahead pass, so a stateful stream would desynchronise.
 */
export function prand(salt, a, b) {
  let h = (Math.imul(salt | 0, 0x9e3779b1) ^ Math.imul(a | 0, 0x85ebca6b) ^ Math.imul(b | 0, 0xc2b2ae35)) >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x7feb352d) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x846ca68b) >>> 0;
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

// ---------------------------------------------------------------------------
// AudioParam envelope writers
// ---------------------------------------------------------------------------

/**
 * Percussive attack/decay envelope on a gain param. Returns the total length in seconds.
 * @param {AudioParam} param
 * @param {number} t0 audio-clock start time
 * @param {number} peak linear peak gain
 * @param {number} attack seconds
 * @param {number} decay seconds to -80 dB
 */
export function envAD(param, t0, peak, attack, decay) {
  const p = Math.max(EPS * 2, peak);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(EPS, t0);
  if (attack > MIN_ATTACK) param.linearRampToValueAtTime(p, t0 + attack);
  else param.setValueAtTime(p, t0 + MIN_ATTACK);
  param.exponentialRampToValueAtTime(EPS, t0 + Math.max(attack, MIN_ATTACK) + decay);
  return Math.max(attack, MIN_ATTACK) + decay;
}

/**
 * Attack / hold-at-peak / release envelope — the shape sustained material (voice, pads,
 * announcer tones) needs. Returns total length in seconds.
 */
export function envAHR(param, t0, peak, attack, hold, release) {
  const p = Math.max(EPS * 2, peak);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(EPS, t0);
  if (attack > MIN_ATTACK) param.linearRampToValueAtTime(p, t0 + attack);
  else param.setValueAtTime(p, t0 + MIN_ATTACK);
  const a = Math.max(attack, MIN_ATTACK);
  param.setValueAtTime(p, t0 + a + hold);
  param.exponentialRampToValueAtTime(EPS, t0 + a + hold + release);
  return a + hold + release;
}

/**
 * Asymmetric swell used by whooshes and gusts: slow rise to a peak partway through, fast fall.
 * @param {number} peakAt fraction of `dur` at which the peak lands, in (0,1)
 */
export function envSwell(param, t0, peak, dur, peakAt = 0.42) {
  const p = Math.max(EPS * 2, peak);
  const tp = t0 + dur * clamp(peakAt, 0.05, 0.95);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(EPS, t0);
  param.exponentialRampToValueAtTime(p, tp);
  param.exponentialRampToValueAtTime(EPS, t0 + dur);
  return dur;
}

/** Exponential glide between two positive values — pitch drops, filter sweeps. */
export function glide(param, t0, from, to, dur) {
  const a = Math.max(1e-3, from);
  const b = Math.max(1e-3, to);
  param.cancelScheduledValues(t0);
  param.setValueAtTime(a, t0);
  param.exponentialRampToValueAtTime(b, t0 + Math.max(MIN_ATTACK, dur));
  return dur;
}

/**
 * Three-point glide: `from` → `mid` at `peakAt` → `to`. This is how a swing's band-pass centre
 * traces limb speed, and how a splash's low-pass rises then settles.
 */
export function glide3(param, t0, from, mid, to, dur, peakAt = 0.4) {
  const tp = Math.max(MIN_ATTACK, dur * clamp(peakAt, 0.05, 0.95));
  param.cancelScheduledValues(t0);
  param.setValueAtTime(Math.max(1e-3, from), t0);
  param.exponentialRampToValueAtTime(Math.max(1e-3, mid), t0 + tp);
  param.exponentialRampToValueAtTime(Math.max(1e-3, to), t0 + Math.max(dur, tp + MIN_ATTACK));
  return dur;
}

// ---------------------------------------------------------------------------
// ShotBuilder — allocation-tracked one-shot graph assembly
// ---------------------------------------------------------------------------

/**
 * Assembles the transient node graph for a single sound.
 *
 * Web Audio source nodes are single-use by specification: an `OscillatorNode` or
 * `AudioBufferSourceNode` cannot be restarted, so a one-shot genuinely requires new nodes.
 * The pooling discipline this subsystem uses instead is:
 *   - all *sample data* is baked once (see the bakers below),
 *   - all *persistent* graph (mix bus, ambience beds, crowd bed, the entire music engine) is
 *     built once and driven purely by scheduled AudioParam automation,
 *   - one-shots are bounded by a hard 48-voice pool, and every node they create is claimed
 *     into the voice record so it can be severed on dispose.
 *
 * A single builder instance is re-bound per shot, so the builder itself never allocates.
 */
export class ShotBuilder {
  constructor() {
    /** @type {any} */
    this.ac = null;
    /** @type {any} */
    this.voice = null;
    /** @type {object|null} */
    this.buffers = null;
    /** @type {any} */
    this.rng = null;
    this.overflow = 0;
  }

  /**
   * @param {object} ac AudioContext-like
   * @param {object} voice pooled voice record with `nodes` / `nodeCount`
   * @param {object} buffers baked buffer bank
   * @param {object} rng an Rng stream owned by src/audio
   */
  bind(ac, voice, buffers, rng) {
    this.ac = ac;
    this.voice = voice;
    this.buffers = buffers;
    this.rng = rng;
    return this;
  }

  /** Records a node against the current voice so dispose() can sever it. */
  claim(node) {
    const v = this.voice;
    if (v) {
      if (v.nodeCount < v.nodes.length) v.nodes[v.nodeCount++] = node;
      else this.overflow++;
    }
    return node;
  }

  gain(value = 1) {
    const n = this.ac.createGain();
    n.gain.value = value;
    return this.claim(n);
  }

  /**
   * @param {'lowpass'|'highpass'|'bandpass'|'peaking'|'notch'|'lowshelf'|'highshelf'|'allpass'} type
   */
  filter(type, freq, q = 1, gainDb = 0) {
    const n = this.ac.createBiquadFilter();
    n.type = type;
    n.frequency.value = Math.max(10, freq);
    n.Q.value = q;
    if (gainDb !== 0) n.gain.value = gainDb;
    return this.claim(n);
  }

  delay(maxSeconds, time) {
    const n = this.ac.createDelay(Math.max(0.001, maxSeconds));
    n.delayTime.value = clamp(time, 0, maxSeconds);
    return this.claim(n);
  }

  shaper(curve, oversample = '2x') {
    const n = this.ac.createWaveShaper();
    n.curve = curve;
    n.oversample = oversample;
    return this.claim(n);
  }

  pan(value = 0) {
    const n = this.ac.createStereoPanner();
    n.pan.value = clamp(value, -1, 1);
    return this.claim(n);
  }

  /** Creates, starts and schedules the stop of an oscillator. Returns it unconnected. */
  osc(type, freq, when, dur) {
    const n = this.ac.createOscillator();
    n.type = type;
    n.frequency.value = Math.max(1, freq);
    this.claim(n);
    n.start(when);
    n.stop(when + dur);
    return n;
  }

  /**
   * Plays a slice of a baked buffer. `nameOrBuffer` may be a key into the buffer bank
   * ('white', 'pink', 'brown', 'velvet') or an AudioBuffer. Loops so the slice can never run
   * off the end, and starts at a stream-chosen offset so repeats never phase-align.
   */
  noise(nameOrBuffer, rate, when, dur, loop = true) {
    const buf = typeof nameOrBuffer === 'string' ? this.buffers[nameOrBuffer] : nameOrBuffer;
    const n = this.ac.createBufferSource();
    n.buffer = buf;
    n.loop = loop;
    n.playbackRate.value = Math.max(0.02, rate);
    this.claim(n);
    const span = buf && buf.duration ? buf.duration : 1;
    const offset = loop && this.rng ? this.rng.next() * span * 0.94 : 0;
    n.start(when, offset);
    n.stop(when + dur);
    return n;
  }
}

// ---------------------------------------------------------------------------
// Bakers — sample data generated once at init
// ---------------------------------------------------------------------------

/** Flat-spectrum noise. Stereo by default so decorrelated hiss needs no extra work. */
export function makeWhiteNoise(ac, rng, seconds = 2.6, channels = 2) {
  const sr = ac.sampleRate;
  const len = Math.max(256, Math.floor(seconds * sr));
  const buf = ac.createBuffer(channels, len, sr);
  for (let c = 0; c < channels; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = rng.next() * 2 - 1;
  }
  return buf;
}

/** -3 dB/octave noise via Paul Kellet's economy filter. The natural bed for rain patter. */
export function makePinkNoise(ac, rng, seconds = 2.6) {
  const sr = ac.sampleRate;
  const len = Math.max(256, Math.floor(seconds * sr));
  const buf = ac.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  let b0 = 0;
  let b1 = 0;
  let b2 = 0;
  let b3 = 0;
  let b4 = 0;
  let b5 = 0;
  for (let i = 0; i < len; i++) {
    const w = rng.next() * 2 - 1;
    b0 = 0.99886 * b0 + w * 0.0555179;
    b1 = 0.99332 * b1 + w * 0.0750759;
    b2 = 0.969 * b2 + w * 0.153852;
    b3 = 0.8665 * b3 + w * 0.3104856;
    b4 = 0.55 * b4 + w * 0.5329522;
    b5 = -0.7616 * b5 - w * 0.016898;
    d[i] = clamp((b0 + b1 + b2 + b3 + b4 + b5 + w * 0.5362) * 0.16, -1, 1);
  }
  return buf;
}

/** -6 dB/octave noise. Wind body and distant-city rumble come from this. */
export function makeBrownNoise(ac, rng, seconds = 3.4) {
  const sr = ac.sampleRate;
  const len = Math.max(256, Math.floor(seconds * sr));
  const buf = ac.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const w = rng.next() * 2 - 1;
    last = (last + 0.021 * w) / 1.021;
    d[i] = clamp(last * 3.6, -1, 1);
  }
  return buf;
}

/**
 * Sparse signed impulses at a controlled density. Velvet noise excites resonators with far
 * less "hiss floor" than white noise, which is what makes cloth grains read as fibres rather
 * than as a filtered shhh.
 */
export function makeVelvetNoise(ac, rng, seconds = 1.2, density = 1600) {
  const sr = ac.sampleRate;
  const len = Math.max(256, Math.floor(seconds * sr));
  const buf = ac.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);
  const stride = Math.max(2, Math.floor(sr / density));
  for (let base = 0; base < len; base += stride) {
    const i = base + Math.floor(rng.next() * stride);
    if (i < len) d[i] = rng.next() < 0.5 ? -1 : 1;
  }
  return buf;
}

/**
 * Glottal-flow-derivative excitation for the formant bank in `synth/voice.js`.
 *
 * A whole number of cycles is baked at an exact integer sample period, so looping the buffer is
 * seamless and `playbackRate` maps linearly to pitch. Each cycle gets its own open quotient,
 * amplitude and asymmetry (jitter / shimmer), which is what stops the result sounding like a
 * synthesiser buzz — the irregularity is the humanity.
 *
 * @returns {{ buffer: AudioBuffer, f0: number }} `f0` is the exact baked pitch; the caller sets
 *   `playbackRate = wantedF0 / f0`.
 */
export function makeGlottalPulse(ac, rng, opts = {}) {
  const {
    f0 = 200, cycles = 80, oq = 0.55, jitter = 0.04, shimmer = 0.14, asym = 0.62,
  } = opts;
  const sr = ac.sampleRate;
  const period = Math.max(8, Math.round(sr / f0));
  const exactF0 = sr / period;
  const len = period * Math.max(4, cycles);
  const buf = ac.createBuffer(1, len, sr);
  const d = buf.getChannelData(0);

  let prev = 0;
  let dc = 0;
  let dcPrev = 0;
  let write = 0;
  for (let c = 0; c < cycles; c++) {
    // Per-cycle character. `wob` is a slow drift so the pitch feels alive across the loop.
    const wob = Math.sin((c / cycles) * TAU * 1.5) * 0.5 + Math.sin((c / cycles) * TAU * 4.7) * 0.2;
    const cycleOq = clamp(oq * (1 + wob * jitter * 3 + (rng.next() - 0.5) * jitter * 2), 0.18, 0.94);
    const amp = clamp(1 + (rng.next() - 0.5) * shimmer * 2 + wob * shimmer * 0.5, 0.25, 1.6);
    const tp = cycleOq * clamp(asym + (rng.next() - 0.5) * 0.08, 0.3, 0.9);
    for (let i = 0; i < period; i++) {
      const p = i / period;
      let g;
      if (p < tp) {
        const u = p / tp;
        g = 3 * u * u - 2 * u * u * u; // smooth opening
      } else if (p < cycleOq) {
        const u = (p - tp) / Math.max(1e-4, cycleOq - tp);
        g = 1 - u * u; // faster closing — the source of the high-frequency energy
      } else {
        g = 0; // closed phase
      }
      const flow = g * amp;
      // Differentiate: the flow DERIVATIVE is the physically correct excitation for a
      // formant filter bank, and it carries the spectral tilt real voices have.
      const deriv = flow - prev;
      prev = flow;
      // One-pole DC block so a long note cannot drift the filter bank off centre.
      dc = deriv - dcPrev + dc * 0.997;
      dcPrev = deriv;
      d[write++] = dc;
    }
  }
  // Pad any remaining tail with silence-free repetition of the closed phase (already zero).
  let peak = 0;
  for (let i = 0; i < len; i++) peak = Math.max(peak, Math.abs(d[i]));
  if (peak > 0) {
    const k = 0.9 / peak;
    for (let i = 0; i < len; i++) d[i] *= k;
  }
  return { buffer: buf, f0: exactF0 };
}

/**
 * Convolution impulse response for the arena: a wet concrete rooftop.
 *
 * Characterised by (a) a discrete early field from the parapet and the stair housing at
 * 6–80 ms, (b) a fairly short diffuse tail because the sky above is an open boundary, and
 * (c) aggressive high-frequency damping that increases with time — water-covered surfaces and
 * falling rain both absorb treble, so the tail collapses toward a dull thud rather than a bright
 * hall wash. The two channels use different noise and a 1.3 ms offset for width.
 */
export function makeImpulseResponse(ac, rng, opts = {}) {
  const {
    seconds = 1.6, rt60 = 1.05, predelay = 0.011, earlyTaps = 18, damping = 0.54, peakTarget = 0.7,
  } = opts;
  const sr = ac.sampleRate;
  const len = Math.max(256, Math.ceil(seconds * sr));
  const ir = ac.createBuffer(2, len, sr);
  const decayK = 6.9078 / Math.max(0.05, rt60);

  for (let c = 0; c < 2; c++) {
    const d = ir.getChannelData(c);
    const pd = Math.floor(predelay * sr) + (c === 1 ? Math.floor(0.0013 * sr) : 0);
    let lp = 0;
    let hp = 0;
    let hpPrev = 0;
    for (let i = pd; i < len; i++) {
      const t = (i - pd) / sr;
      const n = rng.next() * 2 - 1;
      // Time-varying one-pole: the coefficient falls with time, so treble dies first.
      const a = clamp(0.74 - damping * (t / seconds), 0.055, 0.95);
      lp += (n - lp) * a;
      hp = lp - hpPrev + hp * 0.9955; // DC / sub-rumble block
      hpPrev = lp;
      // Echo density ramps in over the first 30 ms so the early field stays discrete.
      const dens = t < 0.03 ? 0.3 + (t / 0.03) * 0.7 : 1;
      d[i] = hp * Math.exp(-decayK * t) * dens;
    }
    // Discrete early reflections: parapet, stair housing, HVAC block.
    for (let k = 0; k < earlyTaps; k++) {
      const tt = 0.006 + Math.pow(rng.next(), 1.35) * 0.078;
      const idx = pd + Math.floor(tt * sr);
      if (idx >= len) continue;
      const sgn = rng.next() < 0.5 ? -1 : 1;
      const amp = (0.9 - (k / earlyTaps) * 0.62) * (0.45 + rng.next() * 0.55) * sgn;
      d[idx] += amp * Math.exp(-decayK * tt);
    }
    d[pd] += 0.55; // the direct parapet slap
  }

  let peak = 0;
  for (let c = 0; c < 2; c++) {
    const d = ir.getChannelData(c);
    for (let i = 0; i < len; i++) {
      const a = Math.abs(d[i]);
      if (a > peak) peak = a;
    }
  }
  if (peak > 0) {
    const k = peakTarget / peak;
    for (let c = 0; c < 2; c++) {
      const d = ir.getChannelData(c);
      for (let i = 0; i < len; i++) d[i] *= k;
    }
  }
  return ir;
}

/**
 * Baked granular crowd bed. Thousands of short windowed grains — each a damped sinusoid mixed
 * with a resonated noise component at a vocally plausible centre frequency — are summed into a
 * seamless loop (grain indices wrap, so there is no discontinuity at the loop point).
 *
 * Baking rather than scheduling live grains is what keeps a 200-voice crowd inside a 0.3 ms
 * CPU budget: at runtime it is three looping buffer sources and some gain automation.
 */
export function makeCrowdBed(ac, rng, opts = {}) {
  const { seconds = 5.0, grains = 1700, peakTarget = 0.72 } = opts;
  const sr = ac.sampleRate;
  const len = Math.max(1024, Math.floor(seconds * sr));
  const buf = ac.createBuffer(2, len, sr);
  const L = buf.getChannelData(0);
  const R = buf.getChannelData(1);

  // Precomputed Hann window: per-sample Math.sin over millions of samples is the one thing
  // that would make this baker visibly slow.
  const WN = 512;
  const win = new Float32Array(WN + 1);
  for (let i = 0; i <= WN; i++) win[i] = 0.5 - 0.5 * Math.cos((i / WN) * TAU);

  const norm = 1 / Math.sqrt(Math.max(1, grains) / 900);
  for (let g = 0; g < grains; g++) {
    const start = Math.floor(rng.next() * len);
    const durS = Math.max(64, Math.floor((0.024 + rng.next() * rng.next() * 0.14) * sr));
    // Crowd voices cluster low with a long bright tail — pow(x,1.7) gives that skew.
    const f = 210 + Math.pow(rng.next(), 1.7) * 2100;
    const w = (TAU * f) / sr;
    const cw = 2 * Math.cos(w);
    let y1 = Math.sin(-w);
    let y2 = Math.sin(-2 * w);
    const amp = (0.05 + rng.next() * 0.17) * norm;
    const pan = rng.next() * 2 - 1;
    const gl = amp * Math.sqrt(clamp01((1 - pan) * 0.5));
    const gr = amp * Math.sqrt(clamp01((1 + pan) * 0.5));
    const noiseMix = 0.3 + rng.next() * 0.55;
    const toneMix = 1 - noiseMix;
    const nzA = 0.18 + rng.next() * 0.42;
    let nz = 0;
    const envMul = Math.exp(-(1.2 + rng.next() * 5.5) / durS);
    let env = 1;
    const wStep = WN / durS;
    for (let i = 0; i < durS; i++) {
      const idx = (start + i) % len;
      const y0 = cw * y1 - y2;
      y2 = y1;
      y1 = y0;
      nz += (rng.next() * 2 - 1 - nz) * nzA;
      env *= envMul;
      const wv = win[(i * wStep) | 0];
      const s = (y0 * toneMix + nz * noiseMix) * wv * env;
      L[idx] += s * gl;
      R[idx] += s * gr;
    }
  }

  let peak = 0;
  for (let i = 0; i < len; i++) {
    const a = Math.abs(L[i]);
    const b = Math.abs(R[i]);
    if (a > peak) peak = a;
    if (b > peak) peak = b;
  }
  if (peak > 0) {
    const k = peakTarget / peak;
    for (let i = 0; i < len; i++) {
      L[i] *= k;
      R[i] *= k;
    }
  }
  return buf;
}

/**
 * Symmetric tanh transfer curve for the final soft-clipper. Even at 0 dBFS input the output
 * cannot exceed 1.0, which is the guarantee that no combo can produce a digital clip.
 */
export function makeTanhCurve(n = 2048, drive = 1.7) {
  const c = new Float32Array(n);
  const k = Math.tanh(drive);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    c[i] = Math.tanh(drive * x) / k;
  }
  return c;
}

/**
 * Asymmetric saturation used sparingly for strain (pain vocalisations, the UI error buzz).
 * The asymmetry adds even harmonics, which read as "effort" rather than "distortion".
 */
export function makeStrainCurve(n = 1024, drive = 2.6, bias = 0.14) {
  const c = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    const y = Math.tanh(drive * (x + bias)) - Math.tanh(drive * bias);
    c[i] = clamp(y / Math.tanh(drive * (1 + bias)), -1, 1);
  }
  return c;
}
